import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GlobalCTA from "@/components/GlobalCTA";
import { Button } from "@/components/ui/button";
import { ArrowRight, Search, Landmark, Target, BarChart3, Shield, Briefcase, FileText, Building2, Scale } from "lucide-react";
import orangeProfile from "@/assets/orange-profile.jpg";
import PageSEO from "@/components/PageSEO";
import { realEstateAgentSchema, realEstateFaqSchema, breadcrumb } from "@/lib/schemas";

const RealEstate = () => {
  const phases = [
    {
      num: "01",
      icon: Landmark,
      title: "Capital Structuring",
      subtitle: "The Foundation",
      problem: "Most founders have \"messy\" income that banks reject.",
      strategy: "We reposition your entrepreneurial income—addressing commingled accounts and 1099 complexity—so underwriters approve it rather than reject it.",
      goal: "Maximizing your borrowing power before you ever look at a property.",
    },
    {
      num: "02",
      icon: Target,
      title: "Asset Deployment",
      subtitle: "The Architecture",
      problem: "Buying properties based on \"hope\" or impulse rather than math.",
      strategy: "Strategic acquisition planning across residential and multi-family assets.",
      goal: "Designing acquisitions that compound over time, ensuring your real estate works harder than you do.",
    },
    {
      num: "03",
      icon: BarChart3,
      title: "Cash-Flow Engineering",
      subtitle: "The System",
      problem: "Owning a \"job\" instead of an investment.",
      strategy: "We analyze rental yield, vacancy rates, and debt service coverage ratios (DSCR) to ensure every asset is self-sustaining.",
      goal: "Creating the passive income streams required to fund your lifestyle and your legacy.",
    },
    {
      num: "04",
      icon: Shield,
      title: "Exit Strategy & Succession",
      subtitle: "The Freedom",
      problem: "Building an empire that crumbles when you stop working.",
      strategy: "Multi-property structuring, entity design, and 1031 exchange positioning to protect equity at every stage.",
      goal: "Success with a successor. We build the systems that allow your wealth to outlive you.",
    },
  ];

  const industries = [
    {
      icon: Briefcase,
      title: "The 1099 Executive & Creative",
      problem: "Multiple income streams that look \"unstable\" to traditional banks.",
      fix: "We position your diverse revenue to show stability and scalability.",
    },
    {
      icon: Building2,
      title: "The Scaling Entrepreneur",
      problem: "Mixed business and personal accounts (commingled income).",
      fix: "We restructure your documentation to create clean income trails that underwriters approve.",
    },
    {
      icon: FileText,
      title: "The Real Estate Investor",
      problem: "High debt-to-income ratios preventing further acquisitions.",
      fix: "Utilizing DSCR and portfolio lending to scale without personal income documentation.",
    },
    {
      icon: Scale,
      title: "The Commercial-to-Residential Transitioner",
      problem: "Moving from SBA-backed assets to personal residential wealth.",
      fix: "Bridging the \"language gap\" between commercial entity structures and residential lending requirements.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="Metro Atlanta Real Estate Agent | Fresh Vision Realty"
        description="Buy, sell, or invest in Metro Atlanta real estate with licensed broker Marquetta Bryant. Serving Cobb, Fulton, DeKalb, Henry & Douglas counties."
        canonical="https://marquettabryant.com/real-estate"
        jsonLd={[realEstateAgentSchema, realEstateFaqSchema, breadcrumb([{name:"Home",path:"/"},{name:"Real Estate",path:"/real-estate"}])]}
      />
      <Header />
      <main id="main-content">
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-accent font-semibold uppercase tracking-widest text-sm">
                Real Estate as Wealth Architecture
              </span>
              <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                Your Vision.{" "}
                <span className="text-primary">
                  Our Strategy.
                </span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Most people buy houses. We build the infrastructure for generational freedom. As your Wealth Architect, I position your assets so underwriters approve them—not reject them.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://freshvisionrealty.com"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button size="lg" variant="premium" className="group">
                    Request a Strategic Audit
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
                <a 
                  href="https://www.freshvisionrealty.com/listing?mode=grid&mapData=%7B%22property_gathering_amount%22%3A10%2C%22zoom%22%3A12%7D&presetParams=%7B%22location%22%3A%7B%22county%22%3A%5B%22Cobb%2C%20GA%22%5D%7D%2C%22propertytype%22%3A%5B%22Single%20Family%20Home%22%5D%2C%22beds%22%3A%224%2C%22%2C%22price%22%3A%22400000%2C%22%7D&listingSortOptions=%5B%7B%22label%22%3A%22Default%22%2C%22value%22%3A%22RELEVANCE%22%7D%2C%7B%22label%22%3A%22Price-High%20To%20Low%22%2C%22value%22%3A%22PRICE_DESC%22%7D%2C%7B%22label%22%3A%22Price-Low%20To%20High%22%2C%22value%22%3A%22PRICE_ASC%22%7D%2C%7B%22label%22%3A%22Newest%20Listings%22%2C%22value%22%3A%22MLS_LIST_DATE_L_DESC%22%7D%2C%7B%22label%22%3A%22Beds%20(Most)%22%2C%22value%22%3A%22BEDROOMS_DESC%22%7D%2C%7B%22label%22%3A%22Baths%20(Most)%22%2C%22value%22%3A%22BATHS_DESC%22%7D%2C%7B%22label%22%3A%22Year%20Built%20(Newest)%22%2C%22value%22%3A%22BUILT_YEAR_DESC%22%7D%2C%7B%22label%22%3A%22Square%20Feet%20(Biggest)%22%2C%22value%22%3A%22SQFT_DESC%22%7D%5D&listingSource=all%20listings" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button size="lg" variant="outline" className="group">
                    <Search className="w-5 h-5 mr-2" />
                    Search Properties
                  </Button>
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-accent/20 to-primary/10 rounded-3xl blur-xl" />
                <img
                  src={orangeProfile}
                  alt="Marquetta Bryant - Wealth Architect"
                  className="relative w-full max-w-md rounded-3xl shadow-luxury"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The MARQUETTA Method */}
      <section className="py-20 md:py-32 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-accent font-semibold uppercase tracking-widest text-sm">
              The Proprietary Process
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mt-4 mb-4">
              The MARQUETTA Method
            </h2>
            <p className="text-xl text-muted-foreground">
              Architecting Your Legacy
            </p>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Most people buy houses; we build the infrastructure for generational freedom.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {phases.map((phase) => (
              <div
                key={phase.num}
                className="bg-background border border-border rounded-xl p-6 hover:shadow-luxury transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-accent font-bold text-lg">{phase.num}</span>
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                    <phase.icon className="w-5 h-5 text-accent" />
                  </div>
                </div>
                <h3 className="font-bold text-lg mb-1">{phase.title}</h3>
                <p className="text-accent text-sm font-medium mb-4">{phase.subtitle}</p>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-semibold text-plum">The Problem:</p>
                    <p className="text-muted-foreground">{phase.problem}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-primary">The Strategy:</p>
                    <p className="text-muted-foreground">{phase.strategy}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-accent">The Goal:</p>
                    <p className="text-muted-foreground">{phase.goal}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <a 
              href="https://freshvisionrealty.com"
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button size="lg" variant="premium" className="group">
                Request a Strategic Audit
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Industries Served / Who We Architect For */}
      <section className="py-20 md:py-32 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-accent font-semibold uppercase tracking-widest text-sm">
              Who We Architect For
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mt-4 mb-4 text-secondary-foreground">
              Clients We Serve
            </h2>
            <p className="text-secondary-foreground/70 text-lg max-w-2xl mx-auto">
              We specialize in the "complex" files that traditional retail banks reject. If your vision is big but your taxes are complicated, we architect the approval.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((item, index) => (
              <div
                key={index}
                className="border border-accent/20 rounded-xl p-6 bg-secondary-foreground/5 hover:bg-secondary-foreground/10 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-bold text-lg text-accent mb-3">{item.title}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-semibold text-secondary-foreground/90">The Problem:</p>
                    <p className="text-secondary-foreground/60">{item.problem}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-accent">The Marquetta Fix:</p>
                    <p className="text-secondary-foreground/60">{item.fix}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Make Your Move?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            We don't just close deals—we open chapters.
          </p>
          <a 
            href="https://freshvisionrealty.com"
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button size="lg" variant="premium" className="group">
              Start My Transaction
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </div>
      </section>

      <GlobalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default RealEstate;
